/**
 * MONSTER TOWN — PALETTE. FROZEN CONTRACT (src/core/*). Additive only.
 *
 * One palette across world, UI and characters. Cozy storybook daylight in the
 * Monster Quest family: warm saturated fills, soft near-black plum ink outlines
 * (NEVER pure black), cream-paper UI.
 *
 * RULES FOR CREWS
 *  - Never write a raw hex literal in feature code. Import from here.
 *  - Need a new colour? ADD a named entry here in a PR-sized edit; never change
 *    an existing value (six crews are colour-matching against these).
 *  - `hex()` -> number for three.js, `css()` -> '#rrggbb' string for DOM/UI.
 */

export const PALETTE = {
  // ---- sky / light -------------------------------------------------------
  skyTop: '#7EC8F2',
  skyHorizon: '#CFEDFB',
  fog: '#CFEDFB',
  sunWarm: '#FFF3D6',
  ambientCool: '#A8C4E0',
  shadow: '#5B6E8C',

  // ---- ink (outlines, text) ---------------------------------------------
  ink: '#2B2438',
  inkSoft: '#4A3E58',

  // ---- ground / nature ---------------------------------------------------
  grassLight: '#8FD46A',
  grass: '#6FBF57',
  grassDark: '#4F9E45',
  dirt: '#B98A5E',
  dirtDark: '#8E6842',
  path: '#E8D3A9',
  pathEdge: '#C9AE80',
  road: '#B9B3A6',
  roadEdge: '#8F8A7E',
  sand: '#F2DEB2',
  water: '#5FC7E8',
  waterDeep: '#2E9BC4',
  rock: '#C9C6BE',
  rockDark: '#9A968D',
  /*
   * ============ WARM STONE (added vr2 — ADDITIVE, nothing above changed) =====
   * `rock`/`rockDark` are true neutrals (r−b of 11 and 13). One boulder is fine;
   * seven of them ringing the pond made *"the only pure-grey objects in a warm
   * town"*, and the same pair paints the zoo's rock habitat. These are the same
   * VALUES carried into the palette's warm family (r−b of 29 and 36), so a
   * stone still reads as stone beside a cream wall and no longer reads as a
   * placeholder beside grass. The cold pair stays for anything that genuinely
   * wants concrete — the road and the fountain still use it.
   */
  stone: '#CFC4B2',
  stoneDark: '#A3947E',

  // ---- plants ------------------------------------------------------------
  trunk: '#8A5A3B',
  trunkDark: '#6A4229',
  leafLight: '#86D06A',
  leaf: '#57A94B',
  leafDark: '#3E8038',
  flowerPink: '#FF8FB1',
  flowerYellow: '#FFD65C',
  flowerWhite: '#FFF6E5',
  flowerPurple: '#B98BE0',
  flowerRed: '#F4726B',

  // ---- buildings ---------------------------------------------------------
  wallCream: '#FFEFD6',
  wallMint: '#CFEFD8',
  wallBlue: '#CBE3FA',
  wallPink: '#FBD6DE',
  wallLilac: '#E2D8F5',
  roofRed: '#E2654F',
  roofTeal: '#4FB6A8',
  roofPurple: '#9A7BD1',
  roofMustard: '#EFB43F',
  woodLight: '#D9A868',
  wood: '#B5814C',
  woodDark: '#7E5533',
  glass: '#BFE7F5',
  glassDark: '#8FC9DE',
  doorWood: '#C57B4E',
  metal: '#B9C2CC',
  metalDark: '#8894A1',

  // ---- interior ----------------------------------------------------------
  floorWood: '#D8A76A',
  floorTile: '#F0E7D8',
  floorRug: '#EE9F6E',
  wallpaperCream: '#FFF4E2',
  wallpaperMint: '#DDF2E4',
  wallpaperBlue: '#D9EAFB',
  fabric: '#F3897F',
  fabricAlt: '#7FB5E8',
  cushion: '#FFD08A',

  // ---- monsters ----------------------------------------------------------
  monsterTeal: '#7FD8C6',
  monsterCoral: '#FF9E7A',
  monsterLilac: '#B49BE8',
  monsterSunny: '#FFD166',
  monsterSky: '#8ECBFF',
  monsterBubblegum: '#FF9EC4',
  monsterLime: '#A8E06B',
  monsterCream: '#F5F0E1',
  monsterBelly: '#FFF3E0',
  horn: '#FFF0C9',
  eyeWhite: '#FFFFFF',
  pupil: '#2B2438',
  blush: '#FF9DAE',

  // ---- food (venue #1) ---------------------------------------------------
  iceVanilla: '#FFF1D2',
  iceStrawberry: '#FF9BB4',
  iceChocolate: '#9A6446',
  iceMint: '#9FE6C8',
  iceBlueberry: '#9FA9EE',
  cone: '#E0A96A',
  cherry: '#E8434F',
  plate: '#FFFDF6',

  // ---- UI ----------------------------------------------------------------
  uiBg: '#FFF8EC',
  uiPanel: '#FFFFFF',
  uiPanelAlt: '#FDEFD8',
  uiBorder: '#2B2438',
  uiText: '#2B2438',
  uiPrimary: '#FF9E4F',
  uiPrimaryDark: '#E07C2E',
  uiAccent: '#4FC1C9',
  uiAccentDark: '#2F9AA2',
  uiOk: '#6FBF57',
  uiWarn: '#FFC23D',
  uiDanger: '#F4726B',
  uiDisabled: '#CFC7BC',
  uiCoin: '#FFC23D',
  uiCoinDark: '#E09A16',
  uiHeart: '#FF6B8B',
  uiGhostOk: '#7BE08F',
  uiGhostBad: '#FF8A8A',
} as const

export type PaletteName = keyof typeof PALETTE

/** '#rrggbb' string — for DOM/CSS/canvas2d textures. */
export function css(name: PaletteName): string {
  return PALETTE[name]
}

/** 0xrrggbb number — for three.js Color / material constructors. */
export function hex(name: PaletteName): number {
  return parseInt(PALETTE[name].slice(1), 16)
}

/** rgba() string with alpha — for canvas2d texture generation and UI. */
export function rgba(name: PaletteName, alpha: number): string {
  const v = hex(name)
  return `rgba(${(v >> 16) & 255},${(v >> 8) & 255},${v & 255},${alpha})`
}

/** Blend two palette entries, t=0 -> a, t=1 -> b. Returns 0xrrggbb. */
export function mix(a: PaletteName, b: PaletteName, t: number): number {
  const av = hex(a)
  const bv = hex(b)
  const k = Math.min(1, Math.max(0, t))
  const r = Math.round(((av >> 16) & 255) * (1 - k) + ((bv >> 16) & 255) * k)
  const g = Math.round(((av >> 8) & 255) * (1 - k) + ((bv >> 8) & 255) * k)
  const bl = Math.round((av & 255) * (1 - k) + (bv & 255) * k)
  return (r << 16) | (g << 8) | bl
}

/** The 8 monster body colours offered in the creator, in menu order. */
export const MONSTER_BODY_COLORS: readonly PaletteName[] = [
  'monsterTeal',
  'monsterCoral',
  'monsterLilac',
  'monsterSunny',
  'monsterSky',
  'monsterBubblegum',
  'monsterLime',
  'monsterCream',
]

/** Art constants that keep the whole game visually consistent. */
export const ART = {
  /** Outline thickness in world units at town scale (inverted-hull). */
  outlineWidth: 0.035,
  /** Toon shading bands. */
  toonBands: 3,
  /** Everything is chunky: min corner radius on box art, in world units. */
  bevel: 0.04,
  /** Canvas-2D texture size for generated textures (keep small: older iPad). */
  textureSize: 128,
} as const
